import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SuperAdminProvider, useSuperAdmin } from "./context/SuperAdminContext";
import { Toaster } from "./components/ui/sonner";
import AuthPage from "./pages/AuthPage";
import BankerDashboard from "./pages/BankerDashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import { Loader2 } from "lucide-react";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Redirect to correct dashboard based on role
    return <Navigate to={user?.role === 'banker' ? '/banker' : '/customer'} replace />;
  }

  return children;
};

// Auth Route - redirects to dashboard if already logged in
const AuthRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={user?.role === 'banker' ? '/banker' : '/customer'} replace />;
  }

  return children;
};

// Super Admin Protected Route
const SuperAdminRoute = ({ children }) => {
  const { admin, loading, isAuthenticated } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/super-admin" replace />;
  }

  return children;
};

// Super Admin Auth Route - redirects if already logged in
const SuperAdminAuthRoute = ({ children }) => {
  const { loading, isAuthenticated } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <AuthRoute>
            <AuthPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/banker" 
        element={
          <ProtectedRoute requiredRole="banker">
            <BankerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer" 
        element={
          <ProtectedRoute requiredRole="customer">
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      {/* Super Admin Routes */}
      <Route 
        path="/super-admin" 
        element={
          <SuperAdminAuthRoute>
            <SuperAdminLogin />
          </SuperAdminAuthRoute>
        } 
      />
      <Route 
        path="/super-admin/dashboard" 
        element={
          <SuperAdminRoute>
            <SuperAdminDashboard />
          </SuperAdminRoute>
        } 
      />
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SuperAdminProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </SuperAdminProvider>
    </AuthProvider>
  );
}

export default App;
